import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import {
  Typography, Table, Tag, Select, DatePicker, Space, Button, Input, Segmented,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';

const { RangePicker } = DatePicker;

const STATUS_COLOR = {
  draft:       'default',
  in_progress: 'blue',
  complete:    'green',
  inactive:    'gold',
  canceled:   'red',
};

const STATUS_OPTIONS = [
  { label: 'Draft',       value: 'draft' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Complete',    value: 'complete' },
  { label: 'Canceled',  value: 'canceled' },
];

const WARNING_COLOR = { '1st': 'blue', '2nd': 'orange', '3rd': 'volcano', 'Final': 'red' };
const WARNING_OPTIONS = [
  { label: '1st Warning',   value: '1st' },
  { label: '2nd Warning',   value: '2nd' },
  { label: '3rd Warning',   value: '3rd' },
  { label: 'Final Warning', value: 'Final' },
];

// Keyed by type ('incident' | 'writeup') since this component is shared.
const _cache = {};

// Quick toggles above the filters (as on Help Desk), each searching at once.
// Status: the toggle (Open by default = Draft and In Progress, the old
// default) unless statuses are picked in the dropdown, which then win; a
// toggle click clears the dropdown. Assigned: no toggle lit when the dropdown
// holds a person.
const STATUS_PRESETS = { open: ['draft', 'in_progress'], complete: ['complete'], all: [] };
const ASSIGNED_PRESETS = ['any', 'me', 'none', 'me_or_none'];

export default function ComplianceListPage({ type }) {
  const navigate     = useNavigate();
  const currentUser  = useContext(UserContext);

  const isWriteup   = type === 'writeup';
  const isAdmin     = currentUser?.Role === 'admin';
  const canEdit     = isWriteup
    ? (currentUser?.CanEditFormalWriteups ?? 0) >= 2
    : (currentUser?.CanEditIncidents ?? 0) >= 2;

  const basePath = isWriteup ? '/compliance/writeups' : '/compliance/incidents';
  const title    = isWriteup ? 'Formal Writeups' : 'Compliance Incidents';

  const restoredRef = useRef(!!_cache[type]);
  const [rows, setRows]                 = useState(() => _cache[type]?.rows             ?? []);
  const [loading, setLoading]           = useState(false);
  const [statusMode, setStatusMode]     = useState(() => _cache[type]?.statusMode       ?? 'open');
  const [statusFilter, setStatus]       = useState(() => _cache[type]?.statusFilter     ?? []);
  const [teamFilter, setTeamFilter]     = useState(() => _cache[type]?.teamFilter       ?? undefined);
  const [employeeFilter, setEmp]        = useState(() => _cache[type]?.employeeFilter   ?? '');
  const [assignedToId, setAssignedToId] = useState(() => _cache[type]?.assignedToId     ?? null);
  const [dateRange, setDateRange]       = useState(() => _cache[type]?.dateRange        ?? null);
  const [warningFilter, setWarning]     = useState(() => _cache[type]?.warningFilter    ?? []);
  const [qaRelatedFilter, setQaRelated] = useState(() => _cache[type]?.qaRelatedFilter  ?? undefined);
  const [teamOptions, setTeamOptions]   = useState([]);
  const [assignedToOpts, setAssignedToOpts] = useState([]);

  // over: filter values to search with now (a quick toggle) before the state
  // holding them has updated.
  const load = useCallback(async (over = {}) => {
    const mode = over.statusMode ?? statusMode;
    const picked = over.statusFilter ?? statusFilter;
    const st = picked?.length ? picked : STATUS_PRESETS[mode];
    const at = 'assignedToId' in over ? over.assignedToId : assignedToId;
    setLoading(true);
    try {
      const params = { type };
      if (st?.length)            params.status              = st.join(',');
      if (teamFilter)            params.team_id             = teamFilter;
      if (at)                    params.assigned_to_user_id = at;
      if (dateRange?.[0])        params.date_from           = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1])        params.date_to             = dateRange[1].format('YYYY-MM-DD');
      if (warningFilter?.length) params.warning             = warningFilter.join(',');
      if (qaRelatedFilter)       params.qa_related          = qaRelatedFilter;
      const res = await api.get('/api/compliance', { params });
      let data = res.data;
      if (employeeFilter.trim()) {
        const q = employeeFilter.trim().toLowerCase();
        data = data.filter((r) => (r.EmployeeName || '').toLowerCase().includes(q));
      }
      setRows(data);
      _cache[type] = { rows: data, statusMode: mode, statusFilter: picked, teamFilter, employeeFilter,
        assignedToId: at, dateRange, warningFilter, qaRelatedFilter };
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [type, statusMode, statusFilter, teamFilter, employeeFilter, assignedToId, dateRange, warningFilter, qaRelatedFilter]);

  const pickStatus = (k) => { setStatusMode(k); setStatus([]); load({ statusMode: k, statusFilter: [] }); };
  const pickAssigned = (k) => { const at = k === 'any' ? null : k; setAssignedToId(at); load({ assignedToId: at }); };

  useEffect(() => { restoredRef.current = false; }, []); // eslint-disable-line
  useEffect(() => { if (_cache[type]) load(); }, []); // eslint-disable-line

  useEffect(() => {
    api.get('/api/teams', { params: { no_client_users: 1 } }).then((r) => {
      const all = r.data || [];
      const vtIds = (currentUser?.VisibleTeams || []).map((t) => t.ID);
      const visible = vtIds.length > 0 ? all.filter((t) => vtIds.includes(t.ID)) : all;
      setTeamOptions(visible.map((t) => ({ label: t.Name, value: t.ID })));
    }).catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    api.get('/api/compliance/filter-options', { params: { type } })
      .then((r) => setAssignedToOpts((r.data.assigned_tos ?? []).map((u) => ({ label: u.name, value: u.id }))))
      .catch(() => {});
  }, [type]);

  const strSort = (key) => (a, b) => (a[key] ?? '').localeCompare(b[key] ?? '');
  const numSort = (key) => (a, b) => (a[key] ?? 0) - (b[key] ?? 0);

  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 65, sorter: numSort('ID') },
    {
      title: 'Employee', dataIndex: 'EmployeeName', key: 'EmployeeName', width: 160, ellipsis: true,
      sorter: strSort('EmployeeName'),
    },
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 140, ellipsis: true,
      sorter: strSort('TeamName'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 150, ellipsis: true,
      sorter: strSort('AssignedToName'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    { title: 'Date of Incident', dataIndex: 'DateOfIncident', key: 'DateOfIncident', width: 130,
      sorter: strSort('DateOfIncident'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 110,
      sorter: strSort('Status'),
      render: (v) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Warning', dataIndex: 'WarningNumber', key: 'WarningNumber', width: 90,
      sorter: strSort('WarningNumber'),
      render: (v) => v ? <Tag color={WARNING_COLOR[v]}>{v}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Warning Expires On', dataIndex: 'WarningExpiresOn', key: 'WarningExpiresOn', width: 170,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      sorter: strSort('WarningExpiresOn'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'QA Related', dataIndex: 'IsQARelated', key: 'IsQARelated', width: 100,
      sorter: (a, b) => (a.IsQARelated ? 1 : 0) - (b.IsQARelated ? 1 : 0),
      render: (v) => v ? 'Yes' : 'No',
    },
    {
      title: 'QA Related Expires On', dataIndex: 'QARelatedExpiresOn', key: 'QARelatedExpiresOn', width: 190,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      sorter: strSort('QARelatedExpiresOn'),
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Issues', key: 'Issues', ellipsis: true,
      sorter: (a, b) => (a.Issues?.join(',') ?? '').localeCompare(b.Issues?.join(',') ?? ''),
      render: (_, r) => (r.Issues || []).length
        ? (r.Issues || []).map((iss) => <Tag key={iss} style={{ marginBottom: 2 }}>{iss}</Tag>)
        : <Typography.Text type="secondary">—</Typography.Text>,
    },
    { title: 'Acknowledged Date', dataIndex: 'AcknowledgedAt', key: 'AcknowledgedAt', width: 175,
      sorter: strSort('AcknowledgedAt'),
      render: (v) => v ? formatTS(v) : <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Created', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 175,
      sorter: strSort('CreatedAt'),
      render: (v) => formatTS(v) },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>{title}</Typography.Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`${basePath}/new`)}>
            New
          </Button>
        )}
      </div>

      <Space wrap size={16} style={{ marginBottom: 12 }}>
        <Segmented
          value={statusFilter?.length ? null : statusMode}
          onChange={pickStatus}
          options={[{ value: 'open', label: 'Open' }, { value: 'complete', label: 'Complete' }, { value: 'all', label: 'All' }]}
        />
        <Segmented
          value={ASSIGNED_PRESETS.includes(assignedToId ?? 'any') ? (assignedToId ?? 'any') : null}
          onChange={pickAssigned}
          options={[{ value: 'any', label: 'Anyone' }, { value: 'me', label: 'Assigned to Me' },
                    { value: 'none', label: 'Unassigned' }, { value: 'me_or_none', label: 'Me + Unassigned' }]}
        />
      </Space>
      <br />
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          mode="multiple" allowClear placeholder="Specific statuses"
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={setStatus}
          maxTagCount="responsive"
          style={{ minWidth: 200 }}
        />
        <Select
          mode="multiple" allowClear placeholder="All warnings"
          options={WARNING_OPTIONS}
          value={warningFilter}
          onChange={setWarning}
          maxTagCount="responsive"
          style={{ minWidth: 160 }}
        />
        <Select
          allowClear placeholder="QA Related"
          options={[{ label: 'QA Related: Yes', value: '1' }, { label: 'QA Related: No', value: '0' }]}
          value={qaRelatedFilter}
          onChange={setQaRelated}
          style={{ width: 160 }}
        />
        <Select
          allowClear showSearch optionFilterProp="label"
          placeholder="All teams"
          options={teamOptions}
          value={teamFilter}
          onChange={setTeamFilter}
          style={{ width: 180 }}
        />
        <Select
          allowClear showSearch optionFilterProp="label"
          placeholder="Assigned To"
          options={[
            { label: 'Me', value: 'me' },
            { label: 'Unassigned', value: 'none' },
            { label: 'Me + Unassigned', value: 'me_or_none' },
            ...assignedToOpts,
          ]}
          value={assignedToId}
          onChange={setAssignedToId}
          style={{ width: 180 }}
        />
        <Input.Search
          placeholder="Employee name"
          allowClear
          value={employeeFilter}
          onChange={(e) => setEmp(e.target.value)}
          onSearch={() => load()}
          style={{ width: 200 }}
        />
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          format="YYYY-MM-DD"
          placeholder={['Incident from', 'Incident to']}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={() => load()} loading={loading}>Search</Button>
      </Space>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} record${t === 1 ? '' : 's'}` }}
        onRow={(r) => ({ onClick: () => navigate(`${basePath}/${r.ID}`), style: { cursor: 'pointer' } })}
        locale={{ emptyText: `No ${title.toLowerCase()} found.` }}
      />
    </>
  );
}
